import getAppName from '@/composables/widget-view-manager/widget-apps'
import type { Message, WorkflowStatus } from './websocket/frames'
import type { WorkflowExecutionPhase } from '@/embed/types'
import type { ViewRef } from './types'

export type ViewContext = {
    partyId?: string
    taskId?: string
    taskDefinitionId?: string
    taskName?: string
    taskStatus?: string
    taskType?: string
    when?: string
    workflowId?: string
    workspaceId?: string
}

export type ViewState = {
    view?: string
    phase?: WorkflowExecutionPhase
    terminal: boolean
    context: ViewContext
    ref: ViewRef
    raw?: Message
}

const workflowPhaseDict: Record<WorkflowStatus, WorkflowExecutionPhase> = {
    CREATED: 'init',
    RUNNING: 'running',
    COMPLETED: 'completed',
    AWAITING: 'init',
    FAILED: 'failed',
    TIMEOUT: 'failed',
    TERMINATED: 'failed',
    PAUSED: 'failed',
    CANCELLED: 'failed',
}

const WORKFLOW_FINISH_STATUSES: WorkflowStatus[] = ['COMPLETED', 'AWAITING', 'FAILED']

export const getWorkflowExecutionPhase = (workflowStatus?: WorkflowStatus): WorkflowExecutionPhase | undefined => {
    return workflowStatus ? workflowPhaseDict[workflowStatus] : undefined
}

export const messageViewState = (message: Message): ViewState => ({
    view: getAppName(message.taskName || message.taskType, message.providerName) || undefined,
    phase: getWorkflowExecutionPhase(message.workflowStatus),
    terminal: false,
    context: {
        partyId: message.partyId,
        taskId: message.taskId,
        taskDefinitionId: message.taskDefinitionId,
        taskName: message.taskName,
        taskStatus: message.taskStatus,
        taskType: message.taskType,
        when: message.when,
        workflowId: message.workflowId,
        workspaceId: message.workspaceId,
    },
    ref: { id: message.taskId },
    raw: message,
})

export const firstMessageViewState = (message: Message): ViewState => {
    if (message.workflowStatus === 'COMPLETED') {
        return { ...messageViewState(message), view: 'workflow-finish', terminal: true }
    }

    if (message.workflowStatus === 'FAILED') {
        return { ...messageViewState(message), view: undefined, terminal: true }
    }

    return messageViewState(message)
}

export const nextMessageViewState = (message: Message): ViewState => {
    const state = messageViewState(message)

    return WORKFLOW_FINISH_STATUSES.includes(message.workflowStatus)
        ? { ...state, view: 'workflow-finish' }
        : state
}
